/**
 * Client for the Live Servers "working-today" roster endpoint (feat-086).
 *
 * Contract (LOCKED): worktrees/feat-086/decisions/0001-live-servers-working-today-contract.md §1.
 *
 * Honesty contract (`feedback_metrics_single_source`): every timestamp on the
 * wire is **raw UTC ISO8601 with a trailing `Z`**. `match_state` and
 * `per_location_health[].status` are computed 100% server-side — this client and
 * the component do FORMATTING + COLORS only. No "is today" / day-bucketing /
 * classification ever happens in TypeScript.
 *
 * Fake mode: when `config.environment === 'fake'` (npm run start:fake) the
 * service returns a canned payload exercising all four match states, all seven
 * feat-274 device-health states + a degraded location, so the page renders
 * without a backend. A `?roster_state=<state>` URL param flips between fixtures
 * (`populated` | `empty` | `error` | `health_unavailable`).
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';

// ---------- Domain types (mirror contract §1 exactly) ----------

/**
 * Match state of a roster row — the server's classification of how a working
 * server reconciles across Square clock-ins and Bespoke shifts. LOCKED enum.
 * Drives the at-risk-first sort order and the status chip / stripe color.
 */
export type MatchState =
  | 'square_only_unmapped'
  | 'square_only'
  | 'bespoke_only'
  | 'both';

/**
 * Per-location Square health status. LOCKED enum. `ok` renders no banner; every
 * other value renders a per-location degraded banner with status-specific copy.
 * `missing_scope` is the expected common v1 state (full banner, not a quiet icon).
 */
export type LocationHealthStatus =
  | 'ok'
  | 'needs_reauth'
  | 'missing_scope'
  | 'stale_poll'
  | 'no_mapping';

export interface LocationHealth {
  location_id: string;
  location_name: string;
  status: LocationHealthStatus;
  /** max(pos_employee_shift.updated) for this location, UTC Z, or null. */
  last_poll_at: string | null;
}

/**
 * Per-server device-health state (feat-274), **classified 100% server-side**.
 *
 * Four of these already existed in `src/utils/liveServerStatus.ts`'s
 * `RecordingState` (feat-080); `no_telemetry`, `offline` and `unknown` are new.
 * Nothing is renamed.
 *
 * The rule this enum exists to enforce: React RENDERS this value and never
 * derives it. `admin/SquareConnectionCard.tsx` states the same rule one domain
 * over — "warning styling is driven by connection state, NEVER elapsed time".
 * On this surface elapsed time is EXPLANATION, never classification. The whole
 * feature exists because a cell reported health it had not measured.
 */
export type DeviceHealthState =
  | 'recording'
  | 'paused'
  | 'connecting'
  | 'stalled'
  | 'no_telemetry'
  | 'offline'
  | 'unknown';

/**
 * The recognised states, in one place, so `coerceDeviceHealthState` can reject
 * anything else without reaching into a presentation map.
 */
export const DEVICE_HEALTH_STATES: readonly DeviceHealthState[] = [
  'recording',
  'paused',
  'connecting',
  'stalled',
  'no_telemetry',
  'offline',
  'unknown',
];

export interface DeviceHealth {
  /** Server-classified. NEVER computed in TypeScript. */
  state: DeviceHealthState;
  /**
   * Last beat on which the device's `bytes_sent` advanced — i.e. the last time
   * the audio PIPELINE was demonstrably alive. UTC ISO8601 `Z`, nullable.
   *
   * **This is not `last_activity_at`.** That one is `MAX(transcript_part.created)`
   * and measures *speech*: per DATA-REQUEST-3.md E2 only 51% of shifts stay under
   * ten minutes of silence at their quietest point, so falling back to it here
   * would put a red chip on every server working a slow afternoon. When
   * `last_audio_at` is absent the sub-line disappears; nothing substitutes for it.
   */
  last_audio_at: string | null;
  /** When `state` began. UTC ISO8601 `Z`, nullable. */
  since: string | null;
  /** Last device telemetry beat of any kind. UTC ISO8601 `Z`, nullable. */
  last_telemetry_at: string | null;
  device_id: string | null;
}

/**
 * Coerce a raw `state` off the wire. A missing field, a null, a typo'd enum
 * value, an older backend or a rolled-back deploy all land on `unknown`.
 *
 * **There is deliberately no branch that produces `recording` from absent data.**
 * A health surface that renders green when it could not reach the health data is
 * the original bug one level up, and worse, because this is the surface people
 * are being told to trust instead of Sentry.
 */
export function coerceDeviceHealthState(raw: unknown): DeviceHealthState {
  return typeof raw === 'string' && (DEVICE_HEALTH_STATES as readonly string[]).includes(raw)
    ? (raw as DeviceHealthState)
    : 'unknown';
}

/**
 * Coerce a raw `device_health` object. Returns `null` ONLY when the key is
 * absent/null on the wire — the component renders that as `unknown` too, but the
 * distinction lets the page tell "health was measured" from "health could not be
 * measured" and raise the §3.5 banner for the latter.
 */
export function coerceDeviceHealth(raw: unknown): DeviceHealth | null {
  if (raw === null || raw === undefined || typeof raw !== 'object') return null;
  const h = raw as Record<string, unknown>;
  const str = (v: unknown): string | null => (v === null || v === undefined ? null : String(v));
  return {
    state: coerceDeviceHealthState(h.state),
    last_audio_at: str(h.last_audio_at),
    since: str(h.since),
    last_telemetry_at: str(h.last_telemetry_at),
    device_id: str(h.device_id),
  };
}

export interface RosterRow {
  match_state: MatchState;
  location_id: string;
  location_name: string;
  /** null ONLY for square_only_unmapped. */
  server_id: string | null;
  /** null ONLY for square_only_unmapped. */
  server_name: string | null;
  /** null ONLY for bespoke_only. */
  vendor_employee_id: string | null;
  /** null ONLY for bespoke_only. */
  vendor_display_name: string | null;
  /** UTC Z; null for bespoke_only. */
  square_first_clock_in_at: string | null;
  /** UTC Z; null for bespoke_only AND when still clocked in (read the flag below). */
  square_latest_clock_out_at: string | null;
  /** true when the latest Square shift today has not ended. false for bespoke_only. */
  square_currently_clocked_in: boolean;
  /** ≥2 drives the "·N shifts" affordance. 0 for bespoke_only. */
  square_shift_count_today: number;
  /** UTC Z; null when no Bespoke shift row today (incl. the live-only edge). */
  bespoke_first_shift_start_at: string | null;
  /**
   * UTC Z. REQUIRED, never null. max() across the source timestamps + (if live)
   * as_of. `MAX(transcript_part.created)` at source — it measures **speech**,
   * which is why the column renders as "Last speech" (feat-274) rather than a
   * generic "Last activity" sitting next to an audio-recency value.
   */
  last_activity_at: string;
  /**
   * feat-274 device health. `null` when the row carried no `device_health` key
   * at all (older backend / health read failed) — rendered as `unknown`, never
   * as healthy and never as a blank cell, because a blank cell reads as "fine".
   */
  device_health: DeviceHealth | null;
}

export interface WorkingTodayResponse {
  /** server clock, UTC ISO8601 Z. The FE de-skews relative times against this. */
  as_of: string;
  /** EXACTLY one entry per allowlisted location the caller can access (RBAC-filtered). */
  per_location_health: LocationHealth[];
  /** MAY be empty (empty-state copy on FE). */
  rows: RosterRow[];
  /**
   * feat-274: false when device health could not be measured at all. Drives the
   * "Microphone health is unavailable" banner. Distinguishes "health measured,
   * all fine" from "health could not be measured" — getting that backwards
   * reproduces the original bug in a new place.
   */
  device_health_available: boolean;
}

// ---------- HTTP plumbing (mirrors squareConnectionService) ----------

function backendUrl(path: string): string {
  const protocol = config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

const isFake = (): boolean => config.environment === 'fake';

/**
 * Coerce a raw row off the wire into a typed `RosterRow`, defending against
 * MySQL DECIMAL/int quirks: `square_shift_count_today` may arrive as a string,
 * `square_currently_clocked_in` may arrive as 0/1. Timestamps stay raw strings.
 */
function coerceRow(r: Record<string, unknown>): RosterRow {
  const str = (v: unknown): string | null => (v === null || v === undefined ? null : String(v));
  return {
    match_state: String(r.match_state ?? 'both') as MatchState,
    location_id: String(r.location_id ?? ''),
    location_name: String(r.location_name ?? ''),
    server_id: str(r.server_id),
    server_name: str(r.server_name),
    vendor_employee_id: str(r.vendor_employee_id),
    vendor_display_name: str(r.vendor_display_name),
    square_first_clock_in_at: str(r.square_first_clock_in_at),
    square_latest_clock_out_at: str(r.square_latest_clock_out_at),
    square_currently_clocked_in: Boolean(r.square_currently_clocked_in),
    square_shift_count_today: Number(r.square_shift_count_today ?? 0),
    bespoke_first_shift_start_at: str(r.bespoke_first_shift_start_at),
    last_activity_at: String(r.last_activity_at ?? ''),
    device_health: coerceDeviceHealth(r.device_health),
  };
}

function coerceHealth(h: Record<string, unknown>): LocationHealth {
  return {
    location_id: String(h.location_id ?? ''),
    location_name: String(h.location_name ?? ''),
    status: String(h.status ?? 'ok') as LocationHealthStatus,
    last_poll_at: h.last_poll_at === null || h.last_poll_at === undefined ? null : String(h.last_poll_at),
  };
}

// ---------- Public API ----------

/**
 * GET /api/admin/live-servers/working-today — the RBAC-scoped working-today
 * roster. In fake mode returns a canned payload (override via `?roster_state=`).
 *
 * `tenantId` (feat-149, ADR 0001) is the caller's `TenantContext.selectedTenant`,
 * passed as an **optional** `?tenant_id=` query param. Scoping is 100%
 * backend-authoritative and additive/narrowing-only: the backend accepts it for
 * admins (narrows the pilot roster to that tenant, or empty for a non-pilot
 * tenant) and ignores it for corporate/manager (always their own token tenant —
 * fail-closed, never widened). When omitted the endpoint behaves exactly as
 * before (token-scoped). The client never filters tenants itself.
 */
export async function getWorkingTodayRoster(tenantId?: string): Promise<WorkingTodayResponse> {
  if (isFake()) return fakeRoster();

  const path = tenantId
    ? `/api/admin/live-servers/working-today?tenant_id=${encodeURIComponent(tenantId)}`
    : '/api/admin/live-servers/working-today';
  const resp = await authedApiFetch(backendUrl(path), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`live-servers/working-today ${resp.status}: ${text}`);
  }
  const raw = (await resp.json()) as {
    as_of?: string;
    per_location_health?: Array<Record<string, unknown>>;
    rows?: Array<Record<string, unknown>>;
    device_health_available?: unknown;
  };
  const rows = (raw.rows ?? []).map(coerceRow);
  return {
    as_of: String(raw.as_of ?? new Date().toISOString()),
    per_location_health: (raw.per_location_health ?? []).map(coerceHealth),
    rows,
    device_health_available: resolveDeviceHealthAvailable(raw.device_health_available, rows),
  };
}

/**
 * feat-274 AC7. `device_health_available` is a payload-SHAPE question ("did this
 * response carry health at all?"), which is the client's to answer — it is not a
 * classification of anyone's health, which stays server-side.
 *
 * - Flag present  → believe it, in both directions.
 * - Flag absent   → an older backend. Infer from shape: available only if at
 *                   least one row actually carried a `device_health` object.
 * - Empty roster  → no rows to judge by, and nothing to be unknown about, so it
 *                   is not reported as unavailable (the banner would be noise on
 *                   a page that is already telling you nobody is clocked in).
 */
function resolveDeviceHealthAvailable(flag: unknown, rows: RosterRow[]): boolean {
  if (flag !== undefined && flag !== null) return Boolean(flag);
  if (rows.length === 0) return true;
  return rows.some((r) => r.device_health !== null);
}

// ---------- Fake mode fixtures ----------
// Mirror the approved mockup (worktrees/feat-086/design/live-servers-roster.html):
// one row per match state + a missing_scope degraded location.

function fakeStateOverride(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('roster_state');
  } catch {
    return null;
  }
}

const LOC_1 = '841841f8-0000-0000-0000-000000000001'; // 1 California
const LOC_580 = 'd3c4378c-0000-0000-0000-000000000002'; // 580 California

function fakeRoster(): WorkingTodayResponse {
  const override = fakeStateOverride();
  const now = Date.now();
  const iso = (msAgo: number): string => new Date(now - msAgo).toISOString();
  const as_of = new Date(now).toISOString();

  if (override === 'error') {
    throw new Error('fake roster_state=error');
  }

  const per_location_health: LocationHealth[] = [
    { location_id: LOC_1, location_name: '1 California', status: 'ok', last_poll_at: iso(30_000) },
    { location_id: LOC_580, location_name: '580 California', status: 'missing_scope', last_poll_at: null },
  ];

  if (override === 'empty') {
    return { as_of, per_location_health, rows: [], device_health_available: true };
  }

  /** Shorthand for a fake `device_health` object. */
  const health = (
    state: DeviceHealthState,
    o: Partial<Omit<DeviceHealth, 'state'>> = {},
  ): DeviceHealth => ({
    state,
    last_audio_at: o.last_audio_at ?? null,
    since: o.since ?? null,
    last_telemetry_at: o.last_telemetry_at ?? null,
    device_id: o.device_id ?? 'd41d8cd9-8f00-b204-e980',
  });

  // Deliberately out of sort order so the severity-ladder sort is exercised.
  const rows: RosterRow[] = [
    // both — recording (1 California)
    {
      match_state: 'both',
      location_id: LOC_1,
      location_name: '1 California',
      server_id: 'srv-emma',
      server_name: 'Emma Larsson',
      vendor_employee_id: 'TM-emma',
      vendor_display_name: 'Emma L.',
      square_first_clock_in_at: iso(8 * 3600_000),
      square_latest_clock_out_at: null,
      square_currently_clocked_in: true,
      square_shift_count_today: 1,
      bespoke_first_shift_start_at: iso(7.5 * 3600_000),
      last_activity_at: iso(5_000),
      device_health: health('recording', {
        last_audio_at: iso(3_000),
        since: iso(7.5 * 3600_000),
        last_telemetry_at: iso(20_000),
      }),
    },
    // bespoke_only — recording (580 California)
    {
      match_state: 'bespoke_only',
      location_id: LOC_580,
      location_name: '580 California',
      server_id: 'srv-priya',
      server_name: 'Priya Venkataraman-Krishnan',
      vendor_employee_id: null,
      vendor_display_name: null,
      square_first_clock_in_at: null,
      square_latest_clock_out_at: null,
      square_currently_clocked_in: false,
      square_shift_count_today: 0,
      bespoke_first_shift_start_at: iso(6 * 3600_000),
      last_activity_at: iso(10_000),
      device_health: health('recording', {
        last_audio_at: iso(1_000),
        since: iso(6 * 3600_000),
        last_telemetry_at: iso(15_000),
      }),
    },
    // square_only_unmapped — clocked in (1 California)
    {
      match_state: 'square_only_unmapped',
      location_id: LOC_1,
      location_name: '1 California',
      server_id: null,
      server_name: null,
      vendor_employee_id: 'TM-maria',
      vendor_display_name: 'Maria G.',
      square_first_clock_in_at: iso(9 * 3600_000),
      square_latest_clock_out_at: null,
      square_currently_clocked_in: true,
      square_shift_count_today: 1,
      bespoke_first_shift_start_at: null,
      last_activity_at: iso(2 * 60_000),
      // Square-only: no Bespoke shift, so there is no device. Not a fault.
      device_health: health('offline'),
    },
    // square_only — mapped, multi-shift, clocked out (1 California)
    {
      match_state: 'square_only',
      location_id: LOC_1,
      location_name: '1 California',
      server_id: 'srv-darnell',
      server_name: 'Darnell Washington',
      vendor_employee_id: 'TM-darnell',
      vendor_display_name: 'Darnell W.',
      square_first_clock_in_at: iso(10 * 3600_000),
      square_latest_clock_out_at: iso(20 * 60_000),
      square_currently_clocked_in: false,
      square_shift_count_today: 2,
      bespoke_first_shift_start_at: null,
      last_activity_at: iso(11 * 60_000),
      device_health: health('offline'),
    },
    // both — clocked in + shift started but device offline (PL #4 diagnostic) (580 California)
    {
      match_state: 'both',
      location_id: LOC_580,
      location_name: '580 California',
      server_id: 'srv-tom',
      server_name: 'Tom Becker',
      vendor_employee_id: 'TM-tom',
      vendor_display_name: 'Tom B.',
      square_first_clock_in_at: iso(11 * 3600_000),
      square_latest_clock_out_at: iso(2 * 3600_000),
      square_currently_clocked_in: false,
      square_shift_count_today: 1,
      bespoke_first_shift_start_at: iso(10.8 * 3600_000),
      last_activity_at: iso(38 * 60_000),
      // Replays shift c25af611-… : socket open, telemetry arriving, bytes frozen.
      device_health: health('stalled', {
        last_audio_at: iso(23.52 * 3600_000),
        since: iso(23.52 * 3600_000),
        last_telemetry_at: iso(30_000),
      }),
    },
    // bespoke_only — the phone stopped reporting entirely (580 California)
    {
      match_state: 'bespoke_only',
      location_id: LOC_1,
      location_name: '1 California',
      server_id: 'srv-aiko',
      server_name: 'Aiko Tanaka',
      vendor_employee_id: null,
      vendor_display_name: null,
      square_first_clock_in_at: null,
      square_latest_clock_out_at: null,
      square_currently_clocked_in: false,
      square_shift_count_today: 0,
      bespoke_first_shift_start_at: iso(2 * 3600_000),
      last_activity_at: iso(7 * 60_000),
      device_health: health('no_telemetry', {
        last_audio_at: iso(6 * 60_000),
        since: iso(6 * 60_000),
        last_telemetry_at: iso(6 * 60_000),
      }),
    },
    // both — paused mid-shift (580 California)
    {
      match_state: 'both',
      location_id: LOC_580,
      location_name: '580 California',
      server_id: 'srv-jonah',
      server_name: 'Jonah Whitfield',
      vendor_employee_id: 'TM-jonah',
      vendor_display_name: 'Jonah W.',
      square_first_clock_in_at: iso(3 * 3600_000),
      square_latest_clock_out_at: null,
      square_currently_clocked_in: true,
      square_shift_count_today: 1,
      bespoke_first_shift_start_at: iso(2.9 * 3600_000),
      last_activity_at: iso(5 * 60_000),
      device_health: health('paused', {
        last_audio_at: iso(4 * 60_000),
        since: iso(4 * 60_000),
        last_telemetry_at: iso(25_000),
      }),
    },
    // both — shift just opened, socket still handshaking (1 California)
    {
      match_state: 'both',
      location_id: LOC_1,
      location_name: '1 California',
      server_id: 'srv-casey',
      server_name: 'Casey Nguyen',
      vendor_employee_id: 'TM-casey',
      vendor_display_name: 'Casey N.',
      square_first_clock_in_at: iso(4 * 60_000),
      square_latest_clock_out_at: null,
      square_currently_clocked_in: true,
      square_shift_count_today: 1,
      bespoke_first_shift_start_at: iso(90_000),
      last_activity_at: iso(90_000),
      device_health: health('connecting', { since: iso(20_000), last_telemetry_at: iso(20_000) }),
    },
  ];

  // AC7 / Panel F — the roster loaded but device health did not. Every row goes
  // to `unknown`; NOT to healthy and NOT to a blank cell.
  if (override === 'health_unavailable') {
    return {
      as_of,
      per_location_health,
      rows: rows.map((r) => ({ ...r, device_health: null })),
      device_health_available: false,
    };
  }

  return { as_of, per_location_health, rows, device_health_available: true };
}
